import type { LibraryFile } from './library';

export interface Opened {
  readonly name: string;
  readonly files: readonly LibraryFile[];
  readonly handle: FileSystemDirectoryHandle | undefined;
}

export interface PendingDrop {
  readonly entries: readonly FileSystemEntry[];
  readonly handles: readonly Promise<FileSystemHandle | null>[];
}

const toLibraryFile = (file: File, path: string): LibraryFile => ({ path, size: file.size, lastModified: file.lastModified, file });

const isFileEntry = (entry: FileSystemEntry): entry is FileSystemFileEntry => entry.isFile;
const isDirectoryEntry = (entry: FileSystemEntry): entry is FileSystemDirectoryEntry => entry.isDirectory;

export function openedFromInput(list: FileList): Opened {
  const files = [...list].map((file) => toLibraryFile(file, file.webkitRelativePath || file.name));
  return { name: files[0]?.path.split('/')[0] ?? 'Files', files, handle: undefined };
}

export function captureDrop(transfer: DataTransfer): PendingDrop {
  const items = [...transfer.items].filter((item) => item.kind === 'file');
  return {
    entries: items.flatMap((item) => item.webkitGetAsEntry() ?? []),
    handles: items.flatMap((item) => item.getAsFileSystemHandle?.() ?? []),
  };
}

async function readDirectory(directory: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = directory.createReader();
  const entries: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (batch.length === 0) return entries;
    entries.push(...batch);
  }
}

async function filesFromEntry(entry: FileSystemEntry): Promise<LibraryFile[]> {
  if (isFileEntry(entry)) {
    const file = await new Promise<File>((resolve, reject) => entry.file(resolve, reject));
    return [toLibraryFile(file, entry.fullPath.replace(/^\//, ''))];
  }
  if (!isDirectoryEntry(entry)) return [];
  const children = await readDirectory(entry);
  return (await Promise.all(children.map(filesFromEntry))).flat();
}

export async function openedFromDrop(drop: PendingDrop): Promise<Opened> {
  const [files, handles] = await Promise.all([
    Promise.all(drop.entries.map(filesFromEntry)),
    Promise.all(drop.handles.map((handle) => handle.catch(() => null))),
  ]);
  const directories = handles.filter((handle): handle is FileSystemDirectoryHandle => handle instanceof FileSystemDirectoryHandle);
  return { name: drop.entries[0]?.name ?? 'Files', files: files.flat(), handle: drop.entries.length === 1 ? directories[0] : undefined };
}

async function filesFromHandle(directory: FileSystemDirectoryHandle, prefix = directory.name): Promise<LibraryFile[]> {
  const children: FileSystemHandle[] = [];
  for await (const child of directory.values()) children.push(child);
  const nested = await Promise.all(
    children.map(async (child) => {
      const path = `${prefix}/${child.name}`;
      if (child instanceof FileSystemFileHandle) return [toLibraryFile(await child.getFile(), path)];
      return child instanceof FileSystemDirectoryHandle ? filesFromHandle(child, path) : [];
    }),
  );
  return nested.flat();
}

export async function openedFromHandle(handle: FileSystemDirectoryHandle): Promise<Opened> {
  return { name: handle.name, files: await filesFromHandle(handle), handle };
}
